import { isBefore, parseISO, subMonths } from 'date-fns'
import React, { useContext, useEffect, useState } from 'react'
import { Index, connectStats } from 'react-instantsearch-dom'

import 'styles/globals.css'
import { FocusedRecipeHook } from '../RecipesInspirationPageTypes'

import { DietIcon } from './DietIcon'
import { InfiniteRecipeHits } from './InfiniteRecipeHits'

import { buildCdnImageUrl } from 'Config'
import { Recipe } from 'Models'
import RecipeModal from 'Pages/JournalPage/components/Modals/RecipeModal/RecipeModal'
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks'
import { fetchRecipeAction, recipesSelector } from 'ReduxStore/recipes'
import { Formatter } from 'Utils'
import { AlgoliaIndexName, getIndexName } from 'Utils/algoliaSearchHelpers'

const RecipeHitContext = React.createContext({} as { initQuery: (id: string) => void })

const useSelectedRecipe = (): FocusedRecipeHook => {
  const dispatch = useAppDispatch()
  const [queryId, setQueryId] = useState<string | undefined>(undefined)
  const queriedRecipes = useAppSelector(recipesSelector)

  const getRecipe = (): Recipe | undefined => {
    return queriedRecipes.find((recipe) => recipe.id === queryId)
  }

  const initQuery = (id: string | undefined): void => {
    setQueryId(id)
  }

  const resetModal = (): void => {
    setQueryId(undefined)
  }

  const fetchRecipe = (id: string): void => {
    dispatch(fetchRecipeAction(id))
  }

  useEffect(() => {
    if (queryId === undefined) {
      return
    }
    if (getRecipe() !== undefined) {
      return
    }
    fetchRecipe(queryId as string)
  })
  return { initQuery, getRecipe, resetModal }
}

export const RecipesInspirationResult: React.FC = () => {
  const { initQuery, getRecipe, resetModal } = useSelectedRecipe()
  const recipe = getRecipe()

  return (
    <>
      <RecipeHitContext.Provider value={{ initQuery }}>
        <div className="col-12 mt-1">
          <RecipeStats />

          <SearchResults />
        </div>

        <RecipeModal
          quantifiedRecipe={{ recipe: recipe, quantity: recipe?.defaultServingsCount, unit: 'PORTION' }}
          onClose={() => resetModal()}
        />
      </RecipeHitContext.Provider>
    </>
  )
}

const RecipeStats = connectStats(({ nbHits }: { nbHits: number }) => (
  <h1 className="mt-3 mb-3">
    {Formatter.formatNumber(nbHits)} {nbHits === 1 ? 'Rezept' : 'Rezepte'} gefunden{nbHits > 0 && ':'}
  </h1>
))

const SearchResults: React.FC = () => {
  return (
    <Index indexName={getIndexName(AlgoliaIndexName.RECIPES_INSPIRATION)}>
      <div className="recipe-grid">
        <InfiniteRecipeHits hitComponent={RecipeHit} />
      </div>
    </Index>
  )
}

const RecipeHit: React.FC<{ hit: Recipe }> = ({ hit }) => {
  const { initQuery } = useContext(RecipeHitContext)
  const { imageUrl, createdAt, ...recipeContent } = hit

  const isNew = isBefore(subMonths(new Date(), 2), parseISO(createdAt || ''))

  return (
    <div onClick={() => initQuery(hit.id)} className="recipecard flexColumn" tabIndex={0}>
      <RecipeImage imageUrl={imageUrl} isNew={isNew} />

      <RecipeContent recipeContent={recipeContent} />
    </div>
  )
}

const RecipeImage: React.FC<{ imageUrl: string | null | undefined; isNew: boolean }> = ({ imageUrl, isNew }) => {
  return (
    <div className="recipecard-image">
      {isNew && <div className="new-badge">Neu</div>}
      <img
        src={
          imageUrl
            ? `${imageUrl}&height=300&quality=60&format=jpg&strip=1`
            : buildCdnImageUrl('standardcard-eatable.svg')
        }
      />
    </div>
  )
}

const RecipeContent: React.FC<{ recipeContent: Recipe }> = ({ recipeContent }) => {
  const { name, preparationTime, nutritionsPerPortion, diet } = recipeContent

  const words = name.trim().split(/\s+/)

  const lastWord = words.pop() ?? ''

  const leading = words.join(' ')

  return (
    <div className="col recipecard-content pt-1">
      <div className="flexColumn">
        <h5 className="col">
          {leading && `${leading} `}

          <span className="title-end">
            <span className="last-word">{lastWord}</span>

            <DietIcon diet={diet} />
          </span>
        </h5>

        <div className="d-flex justify-content-between mt-1">
          <span>{Formatter.formatEnergy(nutritionsPerPortion.energy)}</span>

          <span>{Formatter.formatDurationInMinutes(preparationTime)}</span>
        </div>
      </div>
    </div>
  )
}
